import type { Json } from "@/types/supabase.types";
import type {
  TenantVariableDraft,
  TenantVariableValueDraft,
  TenantVariableView,
  VariableRow,
  VariableValueRow,
} from "./types";

function nullableTrim(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function requiredTrim(value: string, fieldName: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${fieldName} is required`);
  return trimmed;
}

function normalizeOptions(input: TenantVariableDraft): Json {
  if (!Array.isArray(input.variable_options)) {
    throw new Error("Variable options must be an array");
  }
  if (input.variable_options.length === 0) return [];
  if (input.variable_type !== "array") {
    throw new Error("Variable options are only supported for array variables");
  }
  if (input.variable_options.some((option) => typeof option !== "string")) {
    throw new Error("Variable options must be an array of strings");
  }
  return input.variable_options;
}

function normalizeOrder(order: number | undefined): number {
  const value = order ?? 0;
  if (!Number.isInteger(value))
    throw new Error("Variable order must be an integer");
  return value;
}

function normalizeJsonString(value: string): Json {
  try {
    return JSON.parse(value) as Json;
  } catch {
    return value;
  }
}

function normalizeScalarValue(
  variable: VariableRow,
  value: unknown,
): Json | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      if (!variable.variable_is_required) return null;
      throw new Error("Value is required");
    }

    if (
      variable.variable_type === "number" ||
      variable.variable_type === "integer"
    ) {
      const numericValue = Number(trimmed);
      if (!Number.isFinite(numericValue)) {
        throw new Error("Value must be a number");
      }
      if (
        variable.variable_type === "integer" &&
        !Number.isInteger(numericValue)
      ) {
        throw new Error("Value must be an integer");
      }
      return numericValue;
    }

    if (variable.variable_type === "boolean") {
      if (trimmed.toLowerCase() === "true") return true;
      if (trimmed.toLowerCase() === "false") return false;
      return trimmed;
    }

    if (variable.variable_type === "array") {
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    if (variable.variable_type === "json") return normalizeJsonString(trimmed);

    return trimmed;
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  ) {
    return value;
  }

  if (Array.isArray(value)) return value as Json;

  if (typeof value === "object" && value !== null) return value as Json;

  return null;
}

export function toTenantVariableViews(params: {
  variables: VariableRow[];
  values: VariableValueRow[];
  currentTenantId: string;
}): TenantVariableView[] {
  const valuesByVariableId = new Map(
    params.values.map((value) => [value.variable_id, value.variable_value]),
  );

  return params.variables
    .map((variable) => {
      const variableWithHierarchy = variable as VariableRow & {
        parent_variable_id?: string | null;
      };
      const hasExplicitValue = valuesByVariableId.has(variable.variable_id);
      const currentValue = hasExplicitValue
        ? (valuesByVariableId.get(variable.variable_id) ?? null)
        : variable.variable_default;
      const isTenantOwnedDefinition =
        variable.record_tenant_id === params.currentTenantId &&
        variable.tenant_id === params.currentTenantId &&
        variable.analysis_id === null &&
        variable.analysis_step_id === null;

      return {
        ...variable,
        parent_variable_id: variableWithHierarchy.parent_variable_id ?? null,
        current_value: currentValue,
        is_definition_editable: isTenantOwnedDefinition,
        is_definition_deletable: isTenantOwnedDefinition,
      };
    })
    .sort((a, b) => {
      const groupCompare = (a.variable_group ?? "").localeCompare(
        b.variable_group ?? "",
      );
      if (groupCompare !== 0) return groupCompare;
      if (a.variable_order !== b.variable_order) {
        return a.variable_order - b.variable_order;
      }
      return a.variable_name.localeCompare(b.variable_name);
    });
}

export function normalizeTenantVariableDraft(
  input: TenantVariableDraft,
): TenantVariableDraft {
  return {
    ...(input.variable_id ? { variable_id: input.variable_id } : {}),
    variable_name_frontend: requiredTrim(input.variable_name_frontend, "Label"),
    variable_group: nullableTrim(input.variable_group),
    variable_kind: input.variable_kind,
    variable_type: input.variable_type,
    variable_options: normalizeOptions(input),
    variable_default: input.variable_default ?? null,
    variable_is_required: input.variable_is_required ?? false,
    variable_order: normalizeOrder(input.variable_order),
  };
}

export function normalizeTenantVariableValueDraft(params: {
  variable: VariableRow;
  value: unknown;
}): TenantVariableValueDraft {
  return {
    variable_id: params.variable.variable_id,
    value: normalizeScalarValue(params.variable, params.value),
  };
}
